import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { FBXLoader } from 'three/addons/loaders/FBXLoader.js';
import { VRMLoaderPlugin, VRMUtils } from '@pixiv/three-vrm';

const MIXAMO_BONES = {
  mixamorigHips: 'hips', mixamorigSpine: 'spine', mixamorigSpine1: 'chest', mixamorigSpine2: 'upperChest',
  mixamorigNeck: 'neck', mixamorigHead: 'head', mixamorigLeftShoulder: 'leftShoulder',
  mixamorigLeftArm: 'leftUpperArm', mixamorigLeftForeArm: 'leftLowerArm', mixamorigLeftHand: 'leftHand',
  mixamorigRightShoulder: 'rightShoulder', mixamorigRightArm: 'rightUpperArm',
  mixamorigRightForeArm: 'rightLowerArm', mixamorigRightHand: 'rightHand',
  mixamorigLeftUpLeg: 'leftUpperLeg', mixamorigLeftLeg: 'leftLowerLeg', mixamorigLeftFoot: 'leftFoot',
  mixamorigLeftToeBase: 'leftToes', mixamorigRightUpLeg: 'rightUpperLeg',
  mixamorigRightLeg: 'rightLowerLeg', mixamorigRightFoot: 'rightFoot', mixamorigRightToeBase: 'rightToes',
};

const canvas = document.querySelector('#ariya-avatar');
const shell = document.querySelector('#avatar-shell');
const loaderElement = document.querySelector('#avatar-loader');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

let vrm;
let mixer;
let actions = {};
let currentAction;
let speaking = false;
let pointerSeen = false;
const pointer = new THREE.Vector2(0, 0);
const clock = new THREE.Clock();
const deltaQuaternion = new THREE.Quaternion();
const deltaEuler = new THREE.Euler();

function normalizeMixamoName(name) {
  return name.replace('mixamorig:', 'mixamorig').replace('Armature|', '');
}

async function loadVrm(scene) {
  const gltfLoader = new GLTFLoader();
  gltfLoader.register((parser) => new VRMLoaderPlugin(parser));
  const gltf = await gltfLoader.loadAsync('/models/ariya.vrm');
  const loaded = gltf.userData.vrm;
  if (!loaded) throw new Error('ARIYA model did not contain VRM data');
  VRMUtils.rotateVRM0(loaded);
  VRMUtils.removeUnnecessaryVertices(loaded.scene);
  loaded.scene.traverse((object) => {
    if (!object.isMesh) return;
    object.frustumCulled = false;
    const materials = Array.isArray(object.material) ? object.material : [object.material];
    materials.filter(Boolean).forEach((material) => {
      material.transparent = true;
      material.opacity = Math.min(material.opacity ?? 1, 0.9);
      material.depthWrite = true;
      if (material.emissive?.isColor) {
        material.emissive.lerp(new THREE.Color(0x43ddeb), 0.12);
        material.emissiveIntensity = Math.max(material.emissiveIntensity || 0, 0.18);
      }
      material.needsUpdate = true;
    });
  });
  // The canonical ARIYA model's forward axis faces away from this public
  // presentation camera. Turn the complete rig once so visitors meet her face.
  loaded.scene.rotation.y = Math.PI;
  scene.add(loaded.scene);
  return loaded;
}

async function loadMixamoClip(name, url) {
  const asset = await new FBXLoader().loadAsync(url);
  const sourceClip = THREE.AnimationClip.findByName(asset.animations, 'mixamo.com') || asset.animations[0];
  if (!sourceClip || !vrm?.humanoid) return null;

  const sourceHips = asset.getObjectByName('mixamorigHips') || asset.getObjectByName('mixamorig:Hips');
  const normalizedHipsHeight = vrm.humanoid.normalizedRestPose?.hips?.position?.[1] || 1;
  const hipsScale = sourceHips?.position?.y ? normalizedHipsHeight / sourceHips.position.y : 1;
  const tracks = [];
  const restInverse = new THREE.Quaternion();
  const parentRest = new THREE.Quaternion();
  const working = new THREE.Quaternion();

  for (const track of sourceClip.tracks) {
    const [rawRigName, property] = track.name.split('.');
    const rigName = normalizeMixamoName(rawRigName);
    const vrmBoneName = MIXAMO_BONES[rigName];
    if (!vrmBoneName) continue;
    const targetNode = vrm.humanoid.getNormalizedBoneNode(vrmBoneName);
    const sourceNode = asset.getObjectByName(rawRigName);
    if (!targetNode || !sourceNode) continue;

    sourceNode.getWorldQuaternion(restInverse);
    restInverse.invert();
    sourceNode.parent?.getWorldQuaternion(parentRest);

    if (track instanceof THREE.QuaternionKeyframeTrack) {
      const values = Array.from(track.values);
      for (let index = 0; index < values.length; index += 4) {
        working.fromArray(values, index).premultiply(parentRest).multiply(restInverse).toArray(values, index);
      }
      if (vrm.meta?.metaVersion === '0') {
        for (let index = 0; index < values.length; index += 2) values[index] *= -1;
      }
      tracks.push(new THREE.QuaternionKeyframeTrack(`${targetNode.name}.${property}`, track.times, values));
    } else if (track instanceof THREE.VectorKeyframeTrack && vrmBoneName === 'hips') {
      const values = Array.from(track.values, (value, index) => {
        const direction = vrm.meta?.metaVersion === '0' && index % 3 !== 1 ? -1 : 1;
        return value * hipsScale * direction;
      });
      tracks.push(new THREE.VectorKeyframeTrack(`${targetNode.name}.${property}`, track.times, values));
    }
  }

  const clip = new THREE.AnimationClip(name, sourceClip.duration, tracks);
  const action = mixer.clipAction(clip);
  action.enabled = true;
  actions[name] = action;
  return action;
}

function play(name, loop = name === 'idle', fade = 0.35) {
  const next = actions[name];
  if (!next || currentAction === next) return;
  const previous = currentAction;
  next.reset().setEffectiveWeight(1).setEffectiveTimeScale(1);
  next.setLoop(loop ? THREE.LoopRepeat : THREE.LoopOnce, loop ? Infinity : 1);
  next.clampWhenFinished = !loop;
  next.fadeIn(fade).play();
  previous?.fadeOut(fade);
  currentAction = next;
}

function addParticles(scene) {
  const count = reducedMotion ? 34 : 96;
  const positions = new Float32Array(count * 3);
  for (let index = 0; index < count; index += 1) {
    positions[index * 3] = (Math.random() - 0.5) * 1.35;
    positions[index * 3 + 1] = Math.random() * 1.85;
    positions[index * 3 + 2] = (Math.random() - 0.5) * 0.42;
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  const material = new THREE.PointsMaterial({ color: 0x8cf5ff, size: 0.008, transparent: true, opacity: 0.62, depthWrite: false, blending: THREE.AdditiveBlending });
  const particles = new THREE.Points(geometry, material);
  particles.position.y = 0.02;
  scene.add(particles);
  return particles;
}

function applyBoneDelta(name, x, y, z, influence = 1) {
  const bone = vrm?.humanoid?.getRawBoneNode(name);
  if (!bone || influence <= 0) return;
  deltaEuler.set(x * influence, y * influence, z * influence, 'XYZ');
  deltaQuaternion.setFromEuler(deltaEuler);
  bone.quaternion.multiply(deltaQuaternion);
}

function updateTracking(elapsed) {
  if (!vrm || reducedMotion) return;
  const attention = pointerSeen ? 1 : 0;
  const headYaw = THREE.MathUtils.clamp(pointer.x * 0.38, -0.38, 0.38);
  const headPitch = THREE.MathUtils.clamp(-pointer.y * 0.19, -0.19, 0.19);
  applyBoneDelta('neck', headPitch * 0.22, headYaw * 0.3, -headYaw * 0.03, attention);
  applyBoneDelta('head', headPitch * 0.78, headYaw * 0.7, -headYaw * 0.035, attention);

  const reach = Math.min(1, Math.abs(pointer.x) * 0.7 + 0.3);
  const lift = THREE.MathUtils.clamp(pointer.y, -1, 1);
  const followLeft = pointer.x >= 0 ? 1 : 0;
  const followRight = pointer.x < 0 ? 1 : 0;
  applyBoneDelta('leftUpperArm', -0.08 - lift * 0.13, -0.06 * pointer.x, -0.12 - reach * 0.12, followLeft);
  applyBoneDelta('leftLowerArm', 0, 0.04, -0.08 - reach * 0.09, followLeft);
  applyBoneDelta('rightUpperArm', -0.08 - lift * 0.13, -0.06 * pointer.x, 0.12 + reach * 0.12, followRight);
  applyBoneDelta('rightLowerArm', 0, -0.04, 0.08 + reach * 0.09, followRight);

  if (vrm.expressionManager) {
    const mouth = speaking ? 0.18 + Math.abs(Math.sin(elapsed * 10.5)) * 0.42 : 0;
    vrm.expressionManager.setValue('aa', mouth);
  }
}

export async function initializeAvatar() {
  if (!canvas || !shell) return;
  try {
    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true, powerPreference: 'high-performance' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.7));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.18;
    renderer.setClearColor(0x000000, 0);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(27, 1, 0.1, 20);
    camera.position.set(0, 0.93, 3.0);
    camera.lookAt(0, 0.9, 0);
    scene.add(new THREE.HemisphereLight(0xb9f7ff, 0x161226, 2.2));
    const key = new THREE.DirectionalLight(0xffffff, 2.8);
    key.position.set(1.8, 2.5, 2.6);
    scene.add(key);
    const cyanRim = new THREE.PointLight(0x42e9ff, 18, 5);
    cyanRim.position.set(-1.1, 1.15, 1.2);
    scene.add(cyanRim);
    const violetRim = new THREE.PointLight(0x8c63ff, 12, 4);
    violetRim.position.set(1.1, 1.45, 0.3);
    scene.add(violetRim);

    const particles = addParticles(scene);
    vrm = await loadVrm(scene);
    mixer = new THREE.AnimationMixer(vrm.scene);
    await loadMixamoClip('idle', '/animations/idle.fbx');
    play('idle', true, 0);
    loaderElement?.classList.add('is-hidden');

    Promise.allSettled([
      loadMixamoClip('talking', '/animations/talking.fbx'),
      loadMixamoClip('waving', '/animations/waving.fbx'),
    ]).then(() => {
      shell.querySelector('.avatar-contact')?.addEventListener('click', () => {
        if (!speaking) {
          play('waving', false, 0.22);
          window.setTimeout(() => play('idle', true, 0.42), 2500);
        }
      });
    });

    const resize = () => {
      const rect = shell.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      renderer.setSize(rect.width, rect.height, false);
      camera.aspect = rect.width / rect.height;
      camera.updateProjectionMatrix();
    };
    new ResizeObserver(resize).observe(shell);
    resize();

    const render = () => {
      const delta = Math.min(clock.getDelta(), 0.05);
      const elapsed = clock.elapsedTime;
      mixer.update(delta);
      if (!reducedMotion) {
        vrm.scene.position.y = Math.sin(elapsed * 1.35) * 0.008;
        particles.rotation.y = Math.sin(elapsed * 0.16) * 0.12;
        particles.position.y = 0.02 + Math.sin(elapsed * 0.7) * 0.018;
      }
      vrm.update(delta);
      updateTracking(elapsed);
      renderer.render(scene, camera);
      requestAnimationFrame(render);
    };
    render();
  } catch (error) {
    console.error('ARIYA hologram could not initialize:', error);
    loaderElement?.classList.add('is-hidden');
    shell.classList.add('avatar-fallback');
  }
}

window.addEventListener('pointermove', (event) => {
  if (!shell) return;
  const rect = shell.getBoundingClientRect();
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height * 0.42;
  pointer.x = THREE.MathUtils.clamp((event.clientX - centerX) / Math.max(rect.width * 0.65, 1), -1, 1);
  pointer.y = THREE.MathUtils.clamp((event.clientY - centerY) / Math.max(rect.height * 0.55, 1), -1, 1);
  pointerSeen = true;
}, { passive: true });

window.addEventListener('mouseout', (event) => {
  if (!event.relatedTarget) pointerSeen = false;
});

window.addEventListener('ariya:speaking', (event) => {
  speaking = Boolean(event.detail?.active);
  if (speaking && actions.talking) play('talking', true, 0.24);
  if (!speaking && actions.idle) play('idle', true, 0.38);
});
